"use client";

import KibbeFrameworkNote from "@/components/kibbe/KibbeFrameworkNote";

import { Link } from "@/i18n/navigation";
import { trackCtaClick } from "@/lib/analytics";
import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Group,
  Paper,
  Box,
  Divider,
  Accordion,
  Anchor,
  ThemeIcon,
  SimpleGrid,
  Badge,
  Card,
} from "@mantine/core";
import { IconArrowRight, IconCamera } from "@tabler/icons-react";
import { kibbeTypes, kibbeAccentColors } from "../kibbe/kibbeData";

type Faq = { question: string; answer: string };

// The most-searched celebrity typings, with the reasoning spelled out.
// Community-consensus typings; consistent with the celebrity lists on our type pages.
export const CELEBRITIES: {
  name: string;
  typeId: string;
  note: string;
  debated?: string;
}[] = [
  {
    name: "Scarlett Johansson",
    typeId: "soft-natural",
    note: "Width through the shoulders and ribcage with real curve on top. Her most praised looks are draped and relaxed; rigid structure has never suited her.",
    debated: "Sometimes argued Romantic — but the frame width rules it out.",
  },
  {
    name: "Jennifer Lawrence",
    typeId: "flamboyant-natural",
    note: "Tall, long-limbed, blunt-boned. Off the red carpet she defaults to exactly what FN calls for: oversized blazers and unfussy lines.",
    debated: "Soft Natural is the common counter-argument; her length settles it for most typers.",
  },
  {
    name: "Julia Roberts",
    typeId: "flamboyant-natural",
    note: "The FN poster child — broad shoulders, open frame, and that famously relaxed elegance that stiff tailoring always fought.",
  },
  {
    name: "Sophia Loren",
    typeId: "soft-dramatic",
    note: "Tall with lush curve and commanding presence: the original Soft Dramatic. Her draped, waist-anchored gowns remain the SD template.",
  },
  {
    name: "Sofia Vergara",
    typeId: "soft-dramatic",
    note: "Long vertical line plus dramatic curve, always dressed to honor both — bold necklines, fitted drape, maximal glamour that never overwhelms her.",
  },
  {
    name: "Marilyn Monroe",
    typeId: "romantic",
    note: "Delicate sloped shoulders, small bones, pure curve. The reference Romantic — every line of her iconic wardrobe followed the body rather than structuring it.",
  },
  {
    name: "Kate Winslet",
    typeId: "romantic",
    note: "Soft, rounded features over a delicate frame. Community typings split between Romantic and Soft Natural; her delicate bone scale points Romantic.",
    debated: "Soft Natural is a frequent alternative typing.",
  },
  {
    name: "Audrey Hepburn",
    typeId: "flamboyant-gamine",
    note: "Compact yang frame, huge expressive features, deliberate contrast. Cropped trousers and boat necks made her the permanent FG style reference.",
  },
  {
    name: "Winona Ryder",
    typeId: "soft-gamine",
    note: "Petite with soft, doll-like features and that unmistakable pixie quality. Her best 90s looks were fitted, cropped, and playful — textbook SG.",
  },
  {
    name: "Reese Witherspoon",
    typeId: "soft-gamine",
    note: "Small frame, round cheeks, pointed chin: yin-led juxtaposition. Fit-and-flare silhouettes have carried her entire red-carpet history.",
  },
  {
    name: "Princess Diana",
    typeId: "flamboyant-natural",
    note: "Tall and broad-shouldered with unfussy elegance. Her revenge-dress era worked because it honored her frame's sweep instead of constricting it.",
    debated: "Some type her Soft Classic based on early styling — the shoulder width argues otherwise.",
  },
  {
    name: "Grace Kelly",
    typeId: "classic",
    note: "The Classic archetype: perfectly balanced proportions, even features, timeless symmetry. Nothing extreme anywhere — which is itself the type.",
  },
  {
    name: "Cameron Diaz",
    typeId: "flamboyant-natural",
    note: "Athletic width, long limbs, effortless energy. Her career-long preference for relaxed suiting over ornate gowns is FN instinct in action.",
  },
  {
    name: "Cindy Crawford",
    typeId: "flamboyant-natural",
    note: "The supermodel FN: broad-shouldered, long, and blunt-boned, equally at home in a blazer or denim because both accommodate width.",
  },
  {
    name: "Sandra Bullock",
    typeId: "flamboyant-natural",
    note: "Length plus width with a friendly, open presence. Structured-but-relaxed tailoring has always been her strongest look.",
  },
  {
    name: "Halle Berry",
    typeId: "soft-gamine",
    note: "Petite and curvy with bright, animated features. Community typings bounce between SG and TR; her sparkle-over-smolder energy tips it SG.",
    debated: "Theatrical Romantic is the main alternative.",
  },
  {
    name: "Angelina Jolie",
    typeId: "soft-natural",
    note: "Frequently mistyped Dramatic because of her presence, but the frame shows width and her features are lush — yin over a blunt structure.",
    debated: "Soft Dramatic arguments persist; her moderate vertical line is the sticking point.",
  },
  {
    name: "Jennifer Aniston",
    typeId: "natural",
    note: "Moderate height, sturdy athletic frame, relaxed beauty. Thirty years of soft shirting and easy silhouettes — the Natural playbook, executed perfectly.",
  },
  {
    name: "Salma Hayek",
    typeId: "theatrical-romantic",
    note: "Petite, pronounced hourglass, delicately sharp features. The TR combination of lushness and precision is why full glamour never overwhelms her.",
  },
  {
    name: "Jennifer Love Hewitt",
    typeId: "theatrical-romantic",
    note: "Small frame, full curve, fine facial detail. Her most flattering looks have always been fitted and waist-anchored rather than flowing.",
  },
  {
    name: "Blake Lively",
    typeId: "flamboyant-natural",
    note: "Tall with unmistakable shoulder width and glamorous ease. She reads 'effortless' on the red carpet because her stylists dress the frame, not against it.",
  },
  {
    name: "Liv Tyler",
    typeId: "flamboyant-natural",
    note: "Long, broad-framed, and soft-faced — an FN whose ethereal reputation comes from features, while her body line stays firmly Natural family.",
  },
  {
    name: "Amy Adams",
    typeId: "classic",
    note: "Balanced, even proportions with gentle refinement. Typed across the Classic family; the moderation of every feature is the consistent thread.",
    debated: "Soft Classic is the most common refinement.",
  },
  {
    name: "Courteney Cox",
    typeId: "dramatic-classic",
    note: "Moderate frame with a visibly sharp edge — defined jaw, precise features. Crisp tailoring has always photographed better on her than softness.",
  },
  {
    name: "Tilda Swinton",
    typeId: "dramatic",
    note: "The purest Dramatic working today: extreme vertical, narrow sharp bones, otherworldly angularity. Avant-garde structure looks native on her alone.",
  },
  {
    name: "Sarah Jessica Parker",
    typeId: "soft-gamine",
    note: "Petite, playful, with soft curve over a compact frame. Carrie Bradshaw's eclectic wardrobe worked precisely because SJP's SG lines can carry contrast.",
  },
];

export default function KibbeCelebritiesContent({ faqs }: { faqs: Faq[] }) {
  // Group celebrities by type, preserving kibbeTypes order
  const byType = Object.keys(kibbeTypes)
    .map((typeId) => ({
      typeId,
      celebs: CELEBRITIES.filter((c) => c.typeId === typeId),
    }))
    .filter((g) => g.celebs.length > 0);

  return (
    <Container size="xl" py={48}>
      <Stack gap={48}>
        {/* Hero */}
        <Box>
          <Badge size="xl" variant="light" color="pink" mb={16}>
            Celebrity Typings
          </Badge>
          <Title order={1} fz={{ base: 32, md: 44 }} fw={300} mb={16} style={{ letterSpacing: -1 }}>
            Kibbe Celebrities: Famous Examples of Every Type
          </Title>
          <Text fz="xl" c="dimmed" maw={720} style={{ lineHeight: 1.7 }}>
            Studying celebrities is the fastest way to train your eye for Kibbe typing — you can watch the
            same body dressed a thousand ways and see exactly which lines succeed. Below are the most
            commonly searched celebrity typings with the reasoning spelled out, not just the label. One
            caveat before you scroll: these are community-consensus typings. Only David Kibbe verifies
            types, and he has typed very few public figures.
          </Text>
        </Box>
        <KibbeFrameworkNote />
        <Paper p="lg" withBorder radius="md"><Text fw={600}>Community examples, not verified identities</Text><Text size="sm" c="dimmed">The celebrity assignments below have not been individually verified against a primary source. Use the outfits as references; resemblance to a celebrity cannot determine your own identity.</Text></Paper>

        {/* Grouped by type */}
        {byType.map(({ typeId, celebs }) => {
          const type = kibbeTypes[typeId];
          const accent = kibbeAccentColors[typeId];
          return (
            <Box key={typeId}>
              <Group justify="space-between" align="flex-end" mb={16}>
                <Title order={2} fz={24} fw={400}>
                  {type.name} Celebrities
                </Title>
                <Anchor component={Link} href={`/kibbe/${typeId}`} fz="sm">
                  Full {type.name} guide →
                </Anchor>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={16}>
                {celebs.map((celeb) => (
                  <Card key={celeb.name} padding="lg" radius="md" withBorder>
                    <Group justify="space-between" mb={8}>
                      <Text fw={600}>{celeb.name}</Text>
                      <Badge variant="light" color={accent?.primary.split(".")[0] || "gray"} size="sm">
                        {type.name}
                      </Badge>
                    </Group>
                    <Text fz="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
                      {celeb.note}
                    </Text>
                    {celeb.debated && (
                      <Text fz="xs" c="dimmed" mt={8} fs="italic">
                        Debated: {celeb.debated}
                      </Text>
                    )}
                  </Card>
                ))}
              </SimpleGrid>
            </Box>
          );
        })}

        <Divider />

        {/* CTA */}
        <Paper
          p={{ base: "xl", md: 48 }}
          radius="lg"
          ta="center"
          style={{ background: "linear-gradient(135deg, #d6336c 0%, #f783ac 100%)" }}
        >
          <ThemeIcon size={56} radius="xl" variant="light" mb={16} style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>
            <IconCamera size={30} />
          </ThemeIcon>
          <Title order={2} fz={{ base: 24, md: 32 }} fw={400} mb={12} c="white">
            Which Celebrity Shares Your Type?
          </Title>
          <Text fz="md" mb={32} maw={560} mx="auto" style={{ color: "rgba(255,255,255,0.9)", lineHeight: 1.7 }}>
            Upload a photo and our AI reads your vertical line, bone structure, and flesh — then tells you
            your type and which of these celebrities to study for styling.
          </Text>
          <Button
            component={Link}
            href="/kibbe-body-type-test"
            onClick={() => trackCtaClick("kibbe_celeb_test_inline")}
            size="xl"
            rightSection={<IconArrowRight size={20} />}
            styles={{ root: { background: "white", color: "#d6336c", fontWeight: 600 } }}
          >
            Take the Free Kibbe Test
          </Button>
        </Paper>

        {/* FAQ */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={16}>
            Celebrity Kibbe FAQ
          </Title>
          <Accordion variant="separated">
            {faqs.map((faq, i) => (
              <Accordion.Item key={i} value={`faq-${i}`}>
                <Accordion.Control>
                  <Text fw={500}>{faq.question}</Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Text fz="sm" c="dimmed" style={{ lineHeight: 1.7 }}>
                    {faq.answer}
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Box>

        {/* Related */}
        <Box>
          <Title order={3} fz={18} fw={500} mb={16}>
            Keep Going
          </Title>
          <Group gap={12}>
            <Button
              component={Link}
              href="/kibbe-body-type-test"
              onClick={() => trackCtaClick("kibbe_celeb_test_bottom")}
              variant="default"
              size="sm"
            >
              Kibbe Body Type Test
            </Button>
            <Button component={Link} href="/kibbe-body-types-guide" variant="default" size="sm">
              Current & Historical Types Guide
            </Button>
            <Button component={Link} href="/ai-color-analysis" variant="default" size="sm">
              AI Color Analysis
            </Button>
            <Button component={Link} href="/kibbe-outfits" variant="default" size="sm">
              Kibbe Outfit Ideas
            </Button>
          </Group>
        </Box>
      </Stack>
    </Container>
  );
}
